import { useState, useCallback, useMemo } from 'react';
import type { PriceRange, PrimaryAtmosphere, Amenity } from '@wineryxplorer/shared';
import { priceRangeSchema, primaryAtmosphereSchema, amenitiesSchema, z } from '@wineryxplorer/shared';

interface VenueFormData {
  priceRange?: PriceRange;
  primaryAtmosphere?: PrimaryAtmosphere;
  amenities: Amenity[];
}

interface ValidationErrors {
  priceRange?: string[];
  primaryAtmosphere?: string[];
  amenities?: string[];
}

interface UseVenueFormValidationReturn {
  errors: ValidationErrors;
  isValid: boolean;
  validateField: (field: keyof VenueFormData, value: unknown) => string[] | undefined;
  validateAll: (data: VenueFormData) => ValidationErrors;
  clearErrors: () => void;
  clearFieldError: (field: keyof VenueFormData) => void;
}

export const useVenueFormValidation = (): UseVenueFormValidationReturn => {
  const [errors, setErrors] = useState<ValidationErrors>({});

  const validateField = useCallback((field: keyof VenueFormData, value: unknown): string[] | undefined => {
    try {
      switch (field) {
        case 'priceRange':
          if (value !== undefined) {
            priceRangeSchema.parse(value);
          }
          break;
        case 'primaryAtmosphere':
          if (value !== undefined) {
            primaryAtmosphereSchema.parse(value);
          }
          break;
        case 'amenities':
          amenitiesSchema.parse(value);
          break;
        default:
          return undefined;
      }
      return undefined;
    } catch (error: unknown) {
      if (error instanceof z.ZodError) {
        return error.errors.map((err) => err.message);
      }
      return ['Validation error occurred'];
    }
  }, []);

  const validateAll = useCallback((data: VenueFormData): ValidationErrors => {
    const newErrors: ValidationErrors = {};

    // Validate price range
    const priceRangeErrors = validateField('priceRange', data.priceRange);
    if (priceRangeErrors) {
      newErrors.priceRange = priceRangeErrors;
    }

    // Validate primary atmosphere
    const primaryAtmosphereErrors = validateField('primaryAtmosphere', data.primaryAtmosphere);
    if (primaryAtmosphereErrors) {
      newErrors.primaryAtmosphere = primaryAtmosphereErrors;
    }

    // Validate amenities
    const amenitiesErrors = validateField('amenities', data.amenities);
    if (amenitiesErrors) {
      newErrors.amenities = amenitiesErrors;
    }

    setErrors(newErrors);
    return newErrors;
  }, [validateField]);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const clearFieldError = useCallback((field: keyof VenueFormData) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const isValid = useMemo(() => {
    return Object.keys(errors).length === 0;
  }, [errors]);

  return {
    errors,
    isValid,
    validateField,
    validateAll,
    clearErrors,
    clearFieldError,
  };
};